import { useEffect, useState } from "react";

// Reading a table the build cut into files: which file holds a key, fetching it once, and the hook that renders again
// when it lands. Both fetched tables here are sorted and indexed the same way, so this is written once for the pair.

// The index is the first key of every shard as hex, so the shard holding a key is the last one that does not start
// after it. A key below the first shard belongs to no file and is answered without a request being made.
export function shardFor(index: string[], key: bigint): number {
  let low = 0;
  let high = index.length - 1;
  let found = -1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    if (BigInt(`0x${index[middle]}`) <= key) {
      found = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  return found;
}

// The key each shard starts at, which a row's step is measured from and a lookup needs to add back.
export function shardStart(index: string[], shard: number): bigint {
  return BigInt(`0x${index[shard]}`);
}

// Fetched once and kept by address, so a page re-rendering under somebody's typing is not a page asking again. A file
// the network dropped is forgotten rather than kept, which is what makes the next keystroke ask for it again.
export function load<T>(url: string | undefined): Promise<T | undefined> {
  if (!url) return Promise.resolve(undefined);

  const held = files.get(url);
  if (held) return held as Promise<T | undefined>;

  const asked = fetch(url).then((response) => {
    if (!response.ok) throw new Error(`${url}: ${response.status}`);
    return response.json() as Promise<T>;
  });
  // Nothing arrived is the answer rather than a rejection to be caught somewhere else: a shard that would not load
  // and a shard holding no row are told apart by what comes back, and neither of them is an exception.
  const file = asked.catch(() => {
    files.delete(url);
    return undefined;
  });
  files.set(url, file);
  return file;
}

const files = new Map<string, Promise<unknown>>();

// What a page renders with while the shard an answer is in is on its way. A lookup that has no file to ask for is
// answered at once and never reads as loading, which is what keeps a reserved address from showing a spinner.
export interface Reading<T> {
  answer: T | undefined;
  reading: boolean;
}

// The question is a string so that a render under typing asks the same question rather than a new object each time,
// and `read` is called only when it changes.
export function useShard<T>(question: string | undefined, read: (asked: string) => Promise<T | undefined>): Reading<T> {
  const [reading, setReading] = useState<Reading<T>>({ answer: undefined, reading: question !== undefined });

  useEffect(() => {
    if (question === undefined) {
      setReading({ answer: undefined, reading: false });
      return;
    }

    let live = true;
    // The last answer goes as the question changes rather than being held while the next one loads: it is a
    // registration about a different address, and a card that showed it would be reading it out as this one's.
    setReading({ answer: undefined, reading: true });
    read(question).then(
      (answer) => live && setReading({ answer, reading: false }),
      // A shard that would not load leaves the rows absent rather than showing something that is not the registry's.
      () => live && setReading({ answer: undefined, reading: false }),
    );
    return () => {
      live = false;
    };
    // `read` closes over nothing that changes; the question is the whole of what decides the answer.
  }, [question]);

  return reading;
}
